import { IoCloseOutline } from "react-icons/io5";
import { TfiDownload } from "react-icons/tfi";
import { MdOutlineSubdirectoryArrowRight } from "react-icons/md";
import { FiPaperclip } from "react-icons/fi";
import { FaUserShield, FaCommentDots, FaTrash, FaReply } from "react-icons/fa";
import { Pencil } from 'lucide-react';
import React, { useEffect, useState } from "react";
import { downloadPrivacyFile, editComment, getCommentUploadFile, writeAdminComment } from "JS/API";
import { getAdminComment, deleteComment } from "JS/API";

const FloatingList = ({ id, type, isOpen, onClose }) => {
    const [commentText, setCommentText] = useState("");
    const [attachedFile, setAttachedFile] = useState(null);

    const [replyTo, setReplyTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [replyFile, setReplyFile] = useState(null);

    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    // 화면 크기 감지
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        handleResize(); // 초기값 설정
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 모바일에서 열릴 때 body 스크롤 방지
    useEffect(() => {
        if (isMobile && isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobile, isOpen]);

    // 스타일 정의
    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
            display: isMobile ? 'block' : 'none'
        },
        container: {
            width: isMobile ? '100vw' : "350px",
            height: "100vh",
            backgroundColor: "#ffffff",
            position: 'fixed',
            right: isMobile ? (isOpen ? '0' : '-100vw') : '0',
            top: '0',
            boxShadow: isMobile ? '-4px 0 20px rgba(0, 0, 0, 0.2)' : '-4px 0 20px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid #e2e8f0',
            zIndex: 999,
            transition: isMobile ? 'right 0.3s ease-in-out' : 'none',
            transform: !isMobile ? 'none' : (isOpen ? 'translateX(0)' : 'translateX(100%)')
        },
        header: {
            padding: '20px 24px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0
        },
        title: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e293b',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        closeButton: {
            marginLeft: 'auto',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#64748b',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            display: isMobile ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center'
        },
        commentListContainer: {
            flex: 1,
            overflowY: 'auto',
            padding: '16px 0',
            backgroundColor: '#f8fafc'
        },
        loading: {
            textAlign: 'center',
            padding: '40px 24px',
            color: '#64748b',
            fontSize: '14px'
        },
        noComment: {
            textAlign: 'center',
            padding: '40px 24px'
        },
        noCommentText: {
            color: '#64748b',
            fontSize: '14px',
            margin: 0
        },
        commentItem: {
            backgroundColor: '#ffffff',
            margin: '0 16px 12px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        },
        commentHeader: {
            padding: '16px 20px 12px',
            borderBottom: '1px solid #f1f5f9'
        },
        commentAuthor: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#1e293b',
            margin: '0 0 4px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
        },
        commentDate: {
            fontSize: '12px',
            color: '#64748b',
            margin: 0
        },
        commentContent: {
            padding: '0 20px 16px'
        },
        commentText: {
            fontSize: '14px',
            color: '#374151',
            lineHeight: '1.5',
            margin: '0 0 12px 0',
            wordBreak: 'break-word'
        },
        filePreview: {
            marginBottom: '12px'
        },
        fileList: {
            listStyle: 'none',
            padding: 0,
            margin: 0
        },
        fileItem: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: '#f1f5f9',
            borderRadius: '6px',
            margin: '4px 0',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
        },
        fileName: {
            fontSize: '12px',
            color: '#475569',
            flex: 1,
            marginRight: '8px',
            wordBreak: 'break-all'
        },
        downloadButton: {
            backgroundColor: 'transparent',
            border: 'none',
            color: '#3b82f6',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease'
        },
        buttonGroup: {
            display: 'flex',
            gap: '8px',
            padding: '12px 20px',
            borderTop: '1px solid #f1f5f9',
            backgroundColor: '#f8fafc'
        },
        actionButton: {
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
        },
        replyButton: {
            backgroundColor: '#3b82f6',
            color: '#ffffff'
        },
        deleteButton: {
            backgroundColor: '#dc2626',
            color: '#ffffff'
        },
        childComment: {
            margin: '8px 16px 12px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            position: 'relative',
            paddingLeft: '32px'
        },
        replyIcon: {
            position: 'absolute',
            left: '12px',
            top: '16px',
            color: '#64748b'
        },
        inputSection: {
            flexShrink: 0,
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            padding: '20px 24px'
        },
        inputTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e293b',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        textarea: {
            width: '100%',
            minHeight: '80px',
            padding: '12px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            outline: 'none',
            transition: 'border-color 0.2s ease'
        },
        bottomWrap: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '12px'
        },
        fileLabel: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            color: '#64748b',
            fontSize: '12px',
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            transition: 'all 0.2s ease'
        },
        submitButton: {
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '500',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
        },
        replyInput: {
            margin: '12px 20px 16px',
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
        },
        replyTextarea: {
            width: '100%',
            minHeight: '60px',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '13px',
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: '1.4',
            outline: 'none'
        }
    };

    useEffect(() => {
        console.log("commentText 바뀜:", commentText);
    }, [commentText]);

    const handleRegisterComment = async () => {
        if (!commentText.trim()) {
            alert("댓글 내용을 입력해주세요.");
            return;
        }

        const commentPayload = {
            target_type: type,
            target_id: id,
            comment_text: commentText.trim(),
        };

        try {
            const res = await writeAdminComment(commentPayload);
            const commentId = res?.data?.comment_id;

            if (!commentId) throw new Error("댓글 등록에 실패했습니다.");

            if (attachedFile) {
                const formData = new FormData();
                formData.append("comment_id", commentId);
                formData.append("files", attachedFile);

                try {
                    const uploadRes = await getCommentUploadFile(commentId, formData);
                    console.log("파일 업로드 완료:", uploadRes?.data);
                } catch (uploadErr) {
                    console.error("파일 업로드 실패:", uploadErr);
                    alert("파일 업로드에 실패했습니다.");
                }
            }

            alert("댓글이 등록되었습니다.");
            fetchComments()
            setCommentText("");
            setAttachedFile(null);
        } catch (err) {
            console.error("댓글 등록 실패:", err);
            alert("댓글 등록 중 오류가 발생했습니다.");
        }
    };

    const handleReplyComment = async () => {
        if (!replyText.trim()) {
            alert("답글 내용을 입력해주세요.");
            return;
        }

        const payload = {
            parent_id: replyTo,
            target_type: type,
            target_id: id,
            comment_text: replyText.trim(),
        };

        try {
            const res = await writeAdminComment(payload);
            const commentId = res?.data?.comment_id;

            if (commentId && replyFile) {
                const formData = new FormData();
                formData.append("comment_id", commentId);
                formData.append("files", replyFile);

                await getCommentUploadFile(commentId, formData);
            }

            alert("답글이 등록되었습니다.");
            fetchComments()
            setReplyTo(null);
            setReplyText("");
            setReplyFile(null);
        } catch (err) {
            console.error("답글 등록 실패:", err);
            alert("답글 등록 중 오류가 발생했습니다.");
        }
    };

    // === 상태 ===
    const [editingId, setEditingId] = React.useState(null);
    const [editText, setEditText] = React.useState('');

    // === 수정 시작 ===
    const startEdit = (comment) => {
        setEditingId(comment.id);
        setEditText(comment.comment_text || '');
    };

    // === 수정 취소 ===
    const cancelEdit = () => {
        setEditingId(null);
        setEditText('');
    };

    // === 수정 저장 ===
    const handleUpdateComment = async () => {
        if (!editText.trim()) {
            alert('수정 내용을 입력하세요.');
            return;
        }

        try {
            await editComment(editingId, editText.trim());

            alert('댓글이 수정되었습니다.');
            cancelEdit();
            fetchComments();
        } catch (err) {
            console.error('댓글 수정 실패:', err);
            alert('댓글 수정 중 오류가 발생했습니다.');
        }
    };

    const fetchComments = async () => {
        try {
            setLoading(true);
            const response = await getAdminComment(type, id);
            if (response.status === 200 && response.data) {
                setComments(response.data || []);
            }
        } catch (error) {
            console.error("코멘트를 불러오는데 실패했습니다", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPrivacyFile = async (fileName, originalFileName) => {
        try {
            const blob = await downloadPrivacyFile(fileName, originalFileName);

            if (!(blob instanceof Blob)) {
                throw new Error("Response is not a Blob");
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = originalFileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading privacy file:", error);
        }
    };

    // 오버레이에서 mousedown 시작 여부 추적
    const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

    const handleOverlayMouseDown = (e) => {
        if (e.target === e.currentTarget) {
            setMouseDownOnOverlay(true);
        }
    };

    // mousedown과 mouseup이 모두 overlay에서 발생했을 때만 닫기
    const handleOverlayMouseUp = (e) => {
        if (e.target === e.currentTarget && mouseDownOnOverlay) {
            onClose?.();
        }
        setMouseDownOnOverlay(false);
    };

    // ESC 키로 닫기
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [isOpen, onClose]);

    useEffect(() => {
        fetchComments();
    }, [type, id]);

    // 모바일에서 보이지 않을 때는 렌더링하지 않음
    if (isMobile && !isOpen) {
        return null;
    }

    return (
        <>
            {/* 모바일 오버레이 */}
            {isMobile && isOpen && (
                <div
                    style={styles.overlay}
                    onMouseDown={handleOverlayMouseDown}
                    onMouseUp={handleOverlayMouseUp}
                />
            )}

            <div style={styles.container}>
                {/* 헤더 */}
                <div style={styles.header}>
                    <h4 style={styles.title}>
                        <FaUserShield />
                        관리자 메모
                    </h4>
                    {/* 모바일에서만 닫기 버튼 표시 */}
                    <button
                        style={styles.closeButton}
                        onClick={onClose}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        <IoCloseOutline size={20} />
                    </button>
                </div>

                {/* 댓글 목록 */}
                <div style={styles.commentListContainer}>
                    {loading ? (
                        <div style={styles.loading}>로딩 중...</div>
                    ) : comments.length === 0 ? (
                        <div style={styles.noComment}>
                            <p style={styles.noCommentText}>등록된 코멘트가 없습니다.</p>
                        </div>
                    ) : (
                        comments
                            .filter((comment) => comment.parent_id === null)
                            .map((parent) => (
                                <div key={parent.id}>
                                    {/* 부모 댓글 */}
                                    <div style={styles.commentItem}>
                                        <div style={styles.commentHeader}>
                                            <h5 style={styles.commentAuthor}>
                                                <FaUserShield size={12} />
                                                {parent.admin_name}({parent.admin_id})
                                            </h5>
                                            <p style={styles.commentDate}>
                                                {new Date(parent.created_at).toLocaleString()}
                                            </p>
                                        </div>

                                        <div style={styles.commentContent}>
                                            {editingId === parent.id ? (
                                                // ===== 편집 모드 (부모) =====
                                                <div style={{ marginTop: 8 }}>
                                                    <textarea
                                                        style={styles.replyTextarea}
                                                        value={editText}
                                                        onChange={(e) => setEditText(e.target.value)}
                                                        placeholder="수정할 내용을 입력하세요"
                                                        onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }}
                                                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                    />
                                                    <div style={styles.bottomWrap}>
                                                        <button
                                                            style={styles.submitButton}
                                                            onClick={handleUpdateComment}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                                        >
                                                            저장
                                                        </button>
                                                        <button
                                                            style={{ ...styles.actionButton, marginLeft: 8, backgroundColor: '#e2e8f0', color: '#111827' }}
                                                            onClick={cancelEdit}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#cbd5e1'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                                                        >
                                                            취소
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                // ===== 읽기 모드 (부모) =====
                                                <>
                                                    <p style={styles.commentText}>
                                                        {parent.comment_text.split('\n').map((line, index) => (
                                                            <React.Fragment key={index}>
                                                                {line}
                                                                <br />
                                                            </React.Fragment>
                                                        ))}
                                                    </p>

                                                </>
                                            )}

                                            {parent.files?.length > 0 && (
                                                <div style={styles.filePreview}>
                                                    <ul style={styles.fileList}>
                                                        {parent.files.map((file) => (
                                                            <li key={file.id}
                                                                style={styles.fileItem}
                                                                onClick={() => handleDownloadPrivacyFile(file.file_name, file.original_file_name)}
                                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                                                            >
                                                                <span style={styles.fileName}>
                                                                    {file.original_file_name}
                                                                </span>
                                                                <button
                                                                    style={styles.downloadButton}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDownloadPrivacyFile(file.file_name, file.original_file_name);
                                                                    }}
                                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#dbeafe'}
                                                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                                >
                                                                    <TfiDownload />
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>

                                        <div style={styles.buttonGroup}>
                                            <button
                                                style={{ ...styles.actionButton, ...styles.replyButton }}
                                                onClick={() => setReplyTo(parent.id)}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                            >
                                                <FaReply size={10} style={{ marginRight: '4px' }} />
                                                답글
                                            </button>
                                            <button
                                                style={{
                                                    ...styles.actionButton, backgroundColor: '#19c985ff',
                                                    color: 'white'
                                                }}
                                                onClick={() => startEdit(parent)}
                                            >
                                                <Pencil size={10} style={{ marginRight: '4px' }} />
                                                수정
                                            </button>
                                            <button
                                                style={{ ...styles.actionButton, ...styles.deleteButton }}
                                                onClick={async () => {
                                                    const confirmed = window.confirm("삭제하시겠습니까?");
                                                    if (confirmed) {
                                                        await deleteComment(parent.id);
                                                        fetchComments();
                                                    }
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                                            >
                                                <FaTrash size={10} style={{ marginRight: '4px' }} />
                                                삭제
                                            </button>
                                        </div>

                                        {/* 답글 입력 폼 */}
                                        {replyTo === parent.id && (
                                            <div style={styles.replyInput}>
                                                <textarea
                                                    style={styles.replyTextarea}
                                                    value={replyText}
                                                    onChange={(e) => setReplyText(e.target.value)}
                                                    placeholder="답글을 입력하세요"
                                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                />
                                                <div style={styles.bottomWrap}>
                                                    <label style={styles.fileLabel}>
                                                        <FiPaperclip size={14} />
                                                        {replyFile ? replyFile.name : '파일 첨부'}
                                                        <input
                                                            type="file"
                                                            style={{ display: "none" }}
                                                            onClick={(e) => { e.currentTarget.value = ''; }}
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) setReplyFile(file);
                                                            }}
                                                        />
                                                    </label>
                                                    <button
                                                        style={styles.submitButton}
                                                        onClick={handleReplyComment}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                                    >
                                                        등록
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 자식 댓글들 */}
                                    {comments
                                        .filter((child) => child.parent_id === parent.id)
                                        .map((child) => (
                                            <div key={child.id} style={styles.childComment}>
                                                <MdOutlineSubdirectoryArrowRight style={styles.replyIcon} />

                                                <div style={styles.commentHeader}>
                                                    <h5 style={styles.commentAuthor}>
                                                        <FaUserShield size={12} />
                                                        {child.admin_id}
                                                    </h5>
                                                    <p style={styles.commentDate}>
                                                        {new Date(child.created_at).toLocaleString()}
                                                    </p>
                                                </div>

                                                <div style={styles.commentContent}>
                                                    {editingId === child.id ? (
                                                        // ===== 편집 모드 (자식) =====
                                                        <div style={{ marginTop: 8 }}>
                                                            <textarea
                                                                style={styles.replyTextarea}
                                                                value={editText}
                                                                onChange={(e) => setEditText(e.target.value)}
                                                                placeholder="수정할 내용을 입력하세요"
                                                                onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }}
                                                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                            />
                                                            <div style={styles.bottomWrap}>
                                                                <button
                                                                    style={styles.submitButton}
                                                                    onClick={handleUpdateComment}
                                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                                                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                                                >
                                                                    저장
                                                                </button>
                                                                <button
                                                                    style={{ ...styles.actionButton, marginLeft: 8, backgroundColor: '#e2e8f0', color: '#111827' }}
                                                                    onClick={cancelEdit}
                                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#cbd5e1'}
                                                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                                                                >
                                                                    취소
                                                                </button>
                                                            </div>

                                                            {child.files?.length > 0 && (
                                                                <div style={styles.filePreview}>
                                                                    <ul style={styles.fileList}>
                                                                        {child.files.map((file) => (
                                                                            <li key={file.id} style={styles.fileItem}>
                                                                                <span style={styles.fileName}>{file.original_file_name}</span>
                                                                                <button
                                                                                    style={styles.downloadButton}
                                                                                    onClick={() => handleDownloadPrivacyFile(file.file_name, file.original_file_name)}
                                                                                >
                                                                                    <TfiDownload />
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        // ===== 읽기 모드 (자식) =====
                                                        <>
                                                            <p style={styles.commentText}>
                                                                {child.comment_text.split('\n').map((line, index) => (
                                                                    <React.Fragment key={index}>
                                                                        {line}
                                                                        <br />
                                                                    </React.Fragment>
                                                                ))}
                                                            </p>

                                                            {child.files?.length > 0 && (
                                                                <div style={styles.filePreview}>
                                                                    <ul style={styles.fileList}>
                                                                        {child.files.map((file) => (
                                                                            <li key={file.id} style={styles.fileItem}>
                                                                                <span style={styles.fileName}>{file.original_file_name}</span>
                                                                                <button
                                                                                    style={styles.downloadButton}
                                                                                    onClick={() => handleDownloadPrivacyFile(file.file_name, file.original_file_name)}
                                                                                >
                                                                                    <TfiDownload />
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>

                                                <div style={styles.buttonGroup}>
                                                    <button
                                                        style={{
                                                            ...styles.actionButton, backgroundColor: '#19c985ff',
                                                            color: 'white'
                                                        }}
                                                        onClick={() => startEdit(child)}
                                                    >
                                                        <Pencil size={10} style={{ marginRight: '4px' }} />
                                                        수정
                                                    </button>

                                                    <button
                                                        style={{ ...styles.actionButton, ...styles.deleteButton }}
                                                        onClick={async () => {
                                                            const confirmed = window.confirm("삭제하시겠습니까?");
                                                            if (confirmed) {
                                                                await deleteComment(child.id);
                                                                fetchComments();
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                                                    >
                                                        <FaTrash size={10} style={{ marginRight: '4px' }} />
                                                        삭제
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            ))
                    )}
                </div>

                {/* 새 댓글 입력 */}
                <div style={styles.inputSection}>
                    <h4 style={styles.inputTitle}>
                        <FaCommentDots />
                        새 코멘트 작성
                    </h4>
                    <textarea
                        style={styles.textarea}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="댓글을 입력하세요"
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />

                    <div style={styles.bottomWrap}>
                        <label style={styles.fileLabel}>
                            <FiPaperclip size={14} />
                            {attachedFile ? attachedFile.name : '파일 첨부'}
                            <input
                                type="file"
                                style={{ display: "none" }}
                                onClick={(e) => { e.currentTarget.value = ''; }}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) setAttachedFile(file);
                                }}
                            />
                        </label>

                        <button
                            style={styles.submitButton}
                            onClick={handleRegisterComment}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                        >
                            등록
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FloatingList;